import { c as _c } from "react/compiler-runtime";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import InteractiveCatchyCat from "./InteractiveCatchyCat";
const DEFAULT_UTILITY_NAV_ITEMS = [{
  href: "/about",
  label: "About"
}, {
  href: "/privacy",
  label: "Privacy"
}, {
  href: "/terms",
  label: "Terms"
}, {
  href: "/age-suitability",
  label: "Age suitability"
}];

/**
 * @typedef {object} NonAppPageHeadProps
 * @property {string} title
 * @property {string} description
 * @property {string=} canonicalUrl
 * @property {string=} image
 * @property {string=} ogDescription
 * @property {string=} robots
 * @property {string=} twitterTitle
 * @property {string=} twitterDescription
 */

/** @param {NonAppPageHeadProps} props */
export function NonAppPageHead(t0) {
  const $ = _c(32);
  const {
    title,
    description,
    canonicalUrl,
    image,
    ogDescription: t1,
    robots,
    twitterTitle: t2,
    twitterDescription: t3
  } = t0;
  const ogDescription = t1 === undefined ? description : t1;
  const twitterTitle = t2 === undefined ? title : t2;
  const twitterDescription = t3 === undefined ? description : t3;
  let t4;
  if ($[0] !== title) {
    t4 = <title key="title">{title}</title>;
    $[0] = title;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== robots) {
    t5 = robots && <meta name="robots" content={robots} key="robots" />;
    $[2] = robots;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== twitterTitle) {
    t6 = <meta name="twitter:text:title" content={twitterTitle} key="twitterTitle" />;
    $[4] = twitterTitle;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== twitterDescription) {
    t7 = <meta name="twitter:text:description" content={twitterDescription} key="twitterDescription" />;
    $[6] = twitterDescription;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== image) {
    t8 = image && <meta name="twitter:card" content="summary_large_image" key="twitterCard" />;
    $[8] = image;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <meta property="og:type" content="website" key="ogType" />;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== title) {
    t10 = <meta property="og:title" content={title} key="ogTitle" />;
    $[11] = title;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== ogDescription) {
    t11 = <meta property="og:description" content={ogDescription} key="ogDescription" />;
    $[13] = ogDescription;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== description) {
    t12 = <meta name="description" content={description} key="description" />;
    $[15] = description;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== canonicalUrl) {
    t13 = canonicalUrl && <><meta property="og:url" content={canonicalUrl} key="ogUrl" /><link rel="canonical" href={canonicalUrl} key="canonicalUrl" /></>;
    $[17] = canonicalUrl;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== image) {
    t14 = image && <meta property="og:image" content={image} key="ogImage" />;
    $[19] = image;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t12 || $[24] !== t13 || $[25] !== t14 || $[26] !== t4 || $[27] !== t5 || $[28] !== t6 || $[29] !== t7 || $[30] !== t8) {
    t15 = <Head>{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t14}</Head>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  return t15;
}

/**
 * @param {NonAppPageHeadProps & {
 *   children: import("react").ReactNode,
 *   className?: string,
 *   headerClassName?: string,
 *   navItems?: Array<{ href: string, label: string }>,
 *   showNav?: boolean,
 * }} props
 */
export default function NonAppPageShell(t0) {
  const $ = _c(24);
  let children;
  let headProps;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      children,
      className: t1,
      headerClassName: t2,
      navItems: t3,
      showNav: t4,
      ...headProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = headProps;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    children = $[1];
    headProps = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
  }
  const className = t1 === undefined ? "" : t1;
  const headerClassName = t2 === undefined ? "" : t2;
  const navItems = t3 === undefined ? DEFAULT_UTILITY_NAV_ITEMS : t3;
  const showNav = t4 === undefined ? true : t4;
  const {
    pathname
  } = useRouter();
  let t5;
  if ($[7] !== headProps) {
    t5 = <NonAppPageHead {...headProps} />;
    $[7] = headProps;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = `row sticky top-0 z-10 mb-2 flex flex-col gap-2 bg-white/90 px-[20px] pb-2 pt-4 backdrop-blur dark:bg-black/90 ${headerClassName}`;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex items-center justify-between"><Link href="/" aria-label="Catchy home"><InteractiveCatchyCat width={60} height={46} /></Link></div>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== navItems || $[11] !== pathname || $[12] !== showNav) {
    t8 = showNav && navItems?.length > 0 && <nav aria-label="Utility pages" className="-mx-1 flex gap-1 overflow-x-auto pb-2 text-sm">{navItems.map(item => {
        const isCurrent = pathname === item.href;
        return <Link key={item.href} href={item.href} aria-current={isCurrent ? "page" : undefined} className={`shrink-0 rounded-full px-3 py-1.5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 ${isCurrent ? "bg-zinc-100 font-semibold text-zinc-950 dark:bg-zinc-800 dark:text-white" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-white"}`}>{item.label}</Link>;
      })}</nav>;
    $[10] = navItems;
    $[11] = pathname;
    $[12] = showNav;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t6 || $[15] !== t8) {
    t9 = <div className={t6}>{t7}{t8}</div>;
    $[14] = t6;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = `px-[20px] ${className}`;
  let t11;
  if ($[17] !== children || $[18] !== t10) {
    t11 = <div className={t10}>{children}</div>;
    $[17] = children;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11 || $[21] !== t5 || $[22] !== t9) {
    t12 = <>{t5}{t9}{t11}</>;
    $[20] = t11;
    $[21] = t5;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
}